import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import Swal from "sweetalert2";

import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import "../../components/popularProducts/popularproducts.css";

import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import StarIcon from "@mui/icons-material/Star";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";

import IconOne from "../../assets/images/category-1.svg";
import Watermark from "../../assets/images/no_product.jpg";

import { WishlistContext } from "../../context/WishlistContext";
import { CartContext } from "../../context/CartContext";

import { baseURL } from "../../constants/url";

import Header from "../../components/header/Header";
import Subscribe from "../../components/subscribe/Subscribe";
import Footer from "../../components/footer/Footer";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const SearchProductList = () => {
  // ---------------------------------------------------------
  // State
  // ---------------------------------------------------------

  const [products, setProducts] = useState([]);

  const [categories, setCategories] = useState([]);

  const [selectedCategoryId, setSelectedCategoryId] =
    useState(null);

  // First API loading
  const [loading, setLoading] = useState(false);

  // Load More loading
  const [loadMoreLoading, setLoadMoreLoading] =
    useState(false);

  // Backend next page URL
  const [nextUrl, setNextUrl] = useState(null);

  // ---------------------------------------------------------
  // Context
  // ---------------------------------------------------------

  const { fetchWishlistCount } =
    useContext(WishlistContext);

  const { fetchCartCount, cartItems } =
    useContext(CartContext);

  // ---------------------------------------------------------
  // Router
  // ---------------------------------------------------------

  const query = useQuery();

  const navigate = useNavigate();

  const subCategoryId =
    query.get("sub_category");

  const searchTerm =
    query.get("search");

  const { id } = useParams();

  // ---------------------------------------------------------
  // Common Headers
  // ---------------------------------------------------------

  const getHeaders = () => {
    const token =
      localStorage.getItem("authToken");

    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};
  };

  // ---------------------------------------------------------
  // Fetch Categories
  // ---------------------------------------------------------

  const fetchCategories = async () => {
    try {
      const res = await axios.get(
        `${baseURL}common/product_categories/`,
        {
          headers: getHeaders(),
        }
      );

      setCategories(
        res.data || []
      );
    } catch (error) {
      console.error(
        "Error fetching categories:",
        error
      );
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ---------------------------------------------------------
  // Product API response handler
  // ---------------------------------------------------------

  const handleInitialProductResponse = (
    response
  ) => {
    const responseData =
      response?.data;

    /*
      Paginated response:

      {
        count: 100,
        next: "...page=2",
        previous: null,
        results: [...]
      }

      Non-paginated response:
      [...]
    */

    const productList =
      responseData?.results ||
      (Array.isArray(responseData)
        ? responseData
        : []);

    setProducts(productList);

    setNextUrl(
      responseData?.next || null
    );

    console.log(
      "First Page Products:",
      productList.length
    );

    console.log(
      "Total Products:",
      responseData?.count
    );

    console.log(
      "Next URL:",
      responseData?.next
    );
  };

  // ---------------------------------------------------------
  // Fetch All Products - first 50
  // ---------------------------------------------------------

  const fetchProducts = async () => {
    setLoading(true);

    setProducts([]);
    setNextUrl(null);

    try {
      const res = await axios.get(
        `${baseURL}common/products/`,
        {
          headers: getHeaders(),
        }
      );

      handleInitialProductResponse(
        res
      );
    } catch (error) {
      console.error(
        "Error fetching products:",
        error
      );

      setProducts([]);
      setNextUrl(null);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Fetch Category Products - first 50
  // ---------------------------------------------------------

  const fetchProductsByCategory =
    async (
      categoryId,
      subCategoryId = null
    ) => {
      setLoading(true);

      setProducts([]);
      setNextUrl(null);

      try {
        let url =
          `${baseURL}common/products/?category_id=${categoryId}`;

        if (subCategoryId) {
          url +=
            `&sub_category_id=${subCategoryId}`;
        }

        const res =
          await axios.get(url, {
            headers:
              getHeaders(),
          });

        handleInitialProductResponse(
          res
        );
      } catch (error) {
        console.error(
          "Error fetching filtered products:",
          error
        );

        setProducts([]);
        setNextUrl(null);
      } finally {
        setLoading(false);
      }
    };

  // ---------------------------------------------------------
  // Fetch Search Results - first 50
  // ---------------------------------------------------------

  const fetchProductsBySearch =
    async (searchValue) => {
      setLoading(true);

      setProducts([]);
      setNextUrl(null);

      try {
        const res =
          await axios.get(
            `${baseURL}common/products_search/`,
            {
              headers:
                getHeaders(),

              params: {
                q: searchValue,
              },
            }
          );

        handleInitialProductResponse(
          res
        );
      } catch (error) {
        console.error(
          "Error fetching search results:",
          error
        );

        setProducts([]);
        setNextUrl(null);
      } finally {
        setLoading(false);
      }
    };

  // ---------------------------------------------------------
  // Decide which product API to call
  // ---------------------------------------------------------

  useEffect(() => {
    if (searchTerm) {
      setSelectedCategoryId(
        null
      );

      fetchProductsBySearch(
        searchTerm
      );
    } else if (id) {
      setSelectedCategoryId(
        Number(id)
      );

      fetchProductsByCategory(
        id,
        subCategoryId
      );
    } else {
      setSelectedCategoryId(
        null
      );

      fetchProducts();
    }
  }, [
    id,
    subCategoryId,
    searchTerm,
  ]);

  // ---------------------------------------------------------
  // Load Next 50 Products
  // ---------------------------------------------------------

  const loadMoreProducts =
    async () => {
      // No more page
      if (!nextUrl) {
        return;
      }

      // Prevent multiple calls
      if (loadMoreLoading) {
        return;
      }

      setLoadMoreLoading(true);

      try {
        const res =
          await axios.get(
            nextUrl,
            {
              headers:
                getHeaders(),
            }
          );

        const responseData =
          res?.data;

        const newProducts =
          responseData?.results ||
          (Array.isArray(
            responseData
          )
            ? responseData
            : []);

        // Existing products + next 50
        setProducts(
          (prevProducts) => [
            ...prevProducts,
            ...newProducts,
          ]
        );

        // Set next page URL
        setNextUrl(
          responseData?.next ||
            null
        );

        console.log(
          "Loaded Products:",
          newProducts.length
        );

        console.log(
          "Next URL:",
          responseData?.next
        );
      } catch (error) {
        console.error(
          "Error loading more products:",
          error
        );

        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load more products.",
        });
      } finally {
        setLoadMoreLoading(
          false
        );
      }
    };

  // ---------------------------------------------------------
  // Toggle Wishlist
  // ---------------------------------------------------------

  const toggleWishlist =
    async (variantId) => {
      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        Swal.fire({
          icon: "error",
          title:
            "Please Login",
          text: "Please log in to use wishlist.",
          confirmButtonText:
            "Go to Login",
        }).then(() => {
          navigate(
            "/login"
          );
        });

        return;
      }

      try {
        const response =
          await axios.post(
            `${baseURL}common/wishlist_toggle/${variantId}/`,
            {},
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const isWishlisted =
          response?.data?.message
            ?.toLowerCase()
            .includes(
              "added"
            );

        Swal.fire({
          icon: isWishlisted
            ? "success"
            : "info",

          title:
            isWishlisted
              ? "Added to Wishlist"
              : "Removed from Wishlist",

          toast: true,

          position:
            "top-end",

          timer: 2000,

          showConfirmButton:
            false,
        });

        // Update UI locally
        setProducts(
          (prevProducts) =>
            prevProducts.map(
              (product) => ({
                ...product,

                variants:
                  product.variants?.map(
                    (
                      variant
                    ) =>
                      variant.id ===
                      variantId
                        ? {
                            ...variant,
                            is_wishlisted:
                              isWishlisted,
                          }
                        : variant
                  ) || [],
              })
            )
        );

        fetchWishlistCount();
      } catch (error) {
        console.error(
          "Error toggling wishlist:",
          error
        );

        Swal.fire({
          icon: "error",
          title:
            "Something went wrong!",
          text: "Please try again later.",
          toast: true,
          position:
            "top-end",
          timer: 3000,
          showConfirmButton:
            false,
        });
      }
    };

  // ---------------------------------------------------------
  // Add To Cart
  // ---------------------------------------------------------

  const addToCart =
    async (variant) => {
      if (!variant) {
        return;
      }

      if (
        Number(
          variant.stock
        ) === 0
      ) {
        return;
      }

      const token =
        localStorage.getItem(
          "authToken"
        );

      if (!token) {
        Swal.fire({
          icon: "warning",
          title:
            "Login Required",
          text: "Please log in to add products to your cart.",
          confirmButtonText:
            "Go to Login",
        }).then(() => {
          navigate(
            "/login"
          );
        });

        return;
      }

      const alreadyInCart =
        cartItems?.some(
          (item) =>
            item.variant_id ===
              variant.id ||
            item.variant_id?.id ===
              variant.id
        );

      if (alreadyInCart) {
        Swal.fire({
          icon: "info",
          title:
            "Already in Cart",
          text: "This product is already in your cart.",
          timer: 1500,
          showConfirmButton:
            false,
        });

        return;
      }

      try {
        await axios.post(
          `${baseURL}common/cart/add/`,
          {
            variant_id:
              variant.id,
            quantity: 1,
          },
          {
            headers: {
              Authorization:
                `Bearer ${token}`,

              "Content-Type":
                "application/json",
            },
          }
        );

        Swal.fire({
          icon: "success",
          title:
            "Added to Cart!",
          text: "The product has been added to your cart.",
          timer: 1500,
          showConfirmButton:
            false,
        });

        fetchCartCount();
      } catch (error) {
        console.error(
          "Cart Error:",
          error?.response
            ?.data ||
            error.message
        );

        Swal.fire({
          icon: "error",
          title: "Error",

          text:
            error?.response
              ?.data?.message ||
            error?.response
              ?.data?.detail ||
            "Failed to add product to cart.",
        });
      }
    };

  // ---------------------------------------------------------
  // Category click
  // ---------------------------------------------------------

  const handleCategoryClick =
    (categoryId) => {
      let url =
        `/subcategories/${categoryId}`;

      if (subCategoryId) {
        url +=
          `?sub_category=${subCategoryId}`;
      }

      navigate(url);
    };

  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------

  return (
    <>
      <Header />

      {/* Search Text */}

      {searchTerm && (
        <div className="container-fluid mt-3">
          <p>
            Your search results
            for:{" "}

            <strong className="color_green text_capitalize">
              "
              {searchTerm}
              "
            </strong>
          </p>
        </div>
      )}

      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="prod_list_grid mt_20">

              {/* ------------------------- */}
              {/* Categories */}
              {/* ------------------------- */}

              <div className="category-box">

                <h4>
                  Categories
                </h4>

                {categories.map(
                  (cat) => (
                    <div
                      key={
                        cat.id
                      }
                      className={`category-item ${
                        selectedCategoryId ===
                        cat.id
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        handleCategoryClick(
                          cat.id
                        )
                      }
                      style={{
                        cursor:
                          "pointer",
                      }}
                    >

                      <div className="category-left">

                        <img
                          src={
                            cat.image ||
                            IconOne
                          }
                          alt={
                            cat.name
                          }
                          onError={(
                            e
                          ) => {
                            e.currentTarget.src =
                              IconOne;
                          }}
                        />

                        <span>
                          {
                            cat.name
                          }
                        </span>

                      </div>

                    </div>
                  )
                )}

              </div>

              {/* ------------------------- */}
              {/* Product Grid */}
              {/* ------------------------- */}

              <div className="popular_products_grid subcategory">

                {loading ? (
                  <div className="container-fluid text-center pt_40 pb_40">
                    <p>
                      Loading...
                    </p>
                  </div>
                ) : products?.length >
                  0 ? (

                  products.map(
                    (
                      product
                    ) => {
                      const variant =
                        product
                          .variants?.[0];

                      if (
                        !variant
                      ) {
                        return null;
                      }

                      const isOutOfStock =
                        Number(
                          variant.stock
                        ) === 0;

                      const imagePath =
                        variant
                          ?.images?.[0]
                          ?.image ||
                        IconOne;

                      return (
                        <div
                          key={
                            product.id
                          }
                          className="popular_products_grid_in_search"
                        >

                          {/* Product Header */}

                          <div className="products_head">

                            <div className="cart_whislist_icon_box d_flex align_center justify_sp_between">

                              {/* Wishlist */}

                              <button
                                type="button"
                                onClick={() =>
                                  toggleWishlist(
                                    variant.id
                                  )
                                }
                                className="wishlist-btn"
                                style={{
                                  background:
                                    "none",

                                  border:
                                    "none",

                                  cursor:
                                    "pointer",
                                }}
                              >

                                {variant.is_wishlisted ? (
                                  <FavoriteIcon
                                    style={{
                                      color:
                                        "red",
                                    }}
                                  />
                                ) : (
                                  <FavoriteBorderIcon
                                    style={{
                                      color:
                                        "green",
                                    }}
                                  />
                                )}

                              </button>

                              {/* Add To Cart */}

                              <button
                                type="button"
                                onClick={() =>
                                  addToCart(
                                    variant
                                  )
                                }
                                disabled={
                                  isOutOfStock
                                }
                                className="add_to_cart_icons_N mt_5 wishlist-btn font_size14"
                                style={{
                                  background:
                                    "transparent",

                                  border:
                                    "none",

                                  cursor:
                                    isOutOfStock
                                      ? "not-allowed"
                                      : "pointer",

                                  opacity:
                                    isOutOfStock
                                      ? 0.4
                                      : 1,
                                }}
                              >

                                <AddShoppingCartIcon
                                  style={{
                                    color:
                                      "green",
                                  }}
                                />

                              </button>

                            </div>
                          </div>

                          {/* Product Details */}

                          <div
                            className={`products_details ${
                              isOutOfStock
                                ? "out-of-stock"
                                : ""
                            }`}
                          >

                            {/* Product Image */}

                            <div className="products_img d_flex align_center">

                              <Link
                                to={`/product-details/${product.id}`}
                              >

                                <img
                                  src={
                                    imagePath
                                  }
                                  alt={
                                    product.name
                                  }
                                  onError={(
                                    e
                                  ) => {
                                    e.currentTarget.src =
                                      IconOne;
                                  }}
                                />

                              </Link>

                              {isOutOfStock && (
                                <span className="out_of_stock_badge">
                                  Out of
                                  Stock
                                </span>
                              )}

                            </div>

                            {/* Product Info */}

                            <div className="products_info pt_10">

                              {/* Product Name */}

                              <div className="products_name mt_5">

                                <Link
                                  to={`/product-details/${product.id}`}
                                >
                                  {
                                    product.name
                                  }
                                </Link>

                              </div>

                              {/* Price */}

                              <div className="pro_price mt_10 d_flex gap_10 align_center">

                                <h6>
                                  ₹
                                  {
                                    variant.final_price
                                  }
                                  /-
                                </h6>

                                <h6 className="price_cut">
                                  <s>
                                    ₹
                                    {
                                      variant.price
                                    }
                                    /-
                                  </s>
                                </h6>

                                <h6 className="common_color">
                                  (
                                  {
                                    variant.discount_percent
                                  }
                                  % Off)
                                </h6>

                              </div>

                              {/* Rating */}

                              <div className="rating_part d_flex align_center gap_5">

                                <span>
                                  {variant.average_rating ||
                                    "0.0"}
                                </span>

                                <StarIcon />

                              </div>

                              {/* Buy Now */}

                              <div className="products_footer d_flex align_center justify_sp_center">

                                {isOutOfStock ? (

                                  <span
                                    className="buy_btn font_size14 disabled-link"
                                    style={{
                                      background:
                                        "#f38c55",

                                      cursor:
                                        "not-allowed",
                                    }}
                                  >
                                    Out of
                                    Stock
                                  </span>

                                ) : (

                                  <Link
                                    to={`/product-details/${product.id}`}
                                    className="buy_btn font_size14"
                                  >
                                    Buy Now
                                  </Link>

                                )}

                              </div>

                            </div>
                          </div>

                        </div>
                      );
                    }
                  )

                ) : (

                  // No Products

                  <div className="container-fluid text-center pt_40 pb_40">

                    <img
                      src={
                        Watermark
                      }
                      alt="No Products Found"
                      className="no-products-img"
                      style={{
                        width:
                          "300px",

                        opacity:
                          0.3,
                      }}
                    />

                    <h4 className="pt_20">
                      No Products
                      Available
                    </h4>

                  </div>
                )}

                {/* ------------------------- */}
                {/* Load More Button */}
                {/* ------------------------- */}

                {!loading &&
                  nextUrl && (

                    <div className="text-center widthFull mt_30 align_center d_flex justify_sp_center">

                      <button
                        type="button"
                        className="load_more d_flex align_center gap_30"
                        onClick={
                          loadMoreProducts
                        }
                        disabled={
                          loadMoreLoading
                        }
                      >

                        {loadMoreLoading
                          ? "Loading..."
                          : "Load More"}

                        {loadMoreLoading && (
                          <span className="loader"></span>
                        )}

                      </button>

                    </div>
                  )}

              </div>

            </div>
          </div>
        </div>
      </div>

      <Subscribe />

      <Footer />
    </>
  );
};

export default SearchProductList;